'use client';

import React from 'react';
import type { OutfitData, WizardData } from '../lib/types';

interface ErrorScreenProps {
  errorMessage: string;
  fallbackOutfit: OutfitData | null;
  wizardData: Partial<WizardData>;
  onRetry: () => void;
  onReset: () => void;
}

export default function ErrorScreen({
  errorMessage,
  fallbackOutfit,
  wizardData,
  onRetry,
  onReset,
}: ErrorScreenProps) {
  return (
    <div className="min-h-screen flex flex-col items-center" style={{ backgroundColor: '#FAF7F2' }}>
      {/* Error Banner */}
      <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#FEF2F2', border: '1.5px solid #FECACA' }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: '#DC2626' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h1
          className="font-serif-display text-center text-2xl sm:text-3xl mb-3"
          style={{ color: '#1A1A1A' }}
        >
          Something went wrong
        </h1>

        {/* Error message */}
        <p
          className="text-center text-sm sm:text-base mb-2"
          style={{ color: 'hsl(30 8% 38%)' }}
        >
          The AI stylist encountered an issue while generating your outfit.
        </p>
        <p
          className="text-center text-xs sm:text-sm mb-8 px-4 py-3 rounded-xl"
          style={{
            color: '#B91C1C',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
          }}
        >
          {errorMessage}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onRetry}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
            style={{
              backgroundColor: '#1A1A1A',
              color: '#FAF7F2',
              boxShadow: '0 4px 16px rgba(26,26,26,0.18)',
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Try Again
          </button>
          <button
            onClick={onReset}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
            style={{
              backgroundColor: 'transparent',
              color: '#1A1A1A',
              border: '1.5px solid #D1C9BC',
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
            </svg>
            Start Over
          </button>
        </div>
      </div>

      {/* Divider */}
      {fallbackOutfit && (
        <div className="w-full max-w-2xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px" style={{ backgroundColor: '#E5DDD3' }} />
            <span
              className="text-xs font-medium tracking-widest uppercase"
              style={{ color: 'hsl(30 8% 52%)' }}
            >
              Fallback Suggestion
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: '#E5DDD3' }} />
          </div>

          {/* Fallback outfit header */}
          <div className="mb-6 text-center">
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4"
              style={{
                backgroundColor: '#F3EDE4',
                color: '#8B7355',
              }}
            >
              {fallbackOutfit.aesthetic}
            </span>
            <h2
              className="font-serif-display text-xl sm:text-2xl mb-2"
              style={{ color: '#1A1A1A' }}
            >
              {fallbackOutfit.editorialTitle}
            </h2>
            <p
              className="text-sm"
              style={{ color: 'hsl(30 8% 38%)' }}
            >
              {fallbackOutfit.editorialSubtitle}
            </p>
          </div>

          {/* Fallback outfit items */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-12">
            {fallbackOutfit.items.map((item) => (
              <a
                key={item.id}
                href={`https://www.google.com/search?tbm=shop&q=${encodeURIComponent(item.searchQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 p-4 rounded-2xl transition-all duration-200 hover:scale-[1.02] hover:-translate-y-0.5 cursor-pointer"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EDE8E0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                {/* Color dot */}
                <div
                  className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: item.accentColor ?? '#C9A96E' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="text-sm font-semibold leading-snug"
                      style={{ color: '#1A1A1A' }}
                    >
                      {item.name}
                    </p>
                    <span
                      className="text-sm font-bold flex-shrink-0"
                      style={{ color: '#1A1A1A' }}
                    >
                      {item.estimatedPrice}
                    </span>
                  </div>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'hsl(30 8% 52%)' }}
                  >
                    {item.category}
                  </p>
                  <p
                    className="text-xs mt-1.5 leading-relaxed"
                    style={{ color: 'hsl(30 8% 38%)' }}
                  >
                    {item.stylingNote}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
